<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
    show: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'send']);

const store = useStore();

// ===== STATE =====
const messageType = ref('button'); // 'button' | 'list'
const headerText = ref('');
const bodyText = ref('');
const footerText = ref('');

// Reply buttons (max 3)
const buttons = ref([
    { id: 'btn-1', title: '' },
]);

// List
const listButtonText = ref('Ver opções');
const sections = ref([
    {
        title: '',
        rows: [{ id: 'row-1', title: '', description: '' }],
    },
]);

// Template state
const showTemplateMenu = ref(false);
const showSaveDialog = ref(false);
const saveName = ref('');
const saveScope = ref('global');
const isSaving = ref(false);

// ===== TEMPLATES =====
const templates = computed(() => store.getters['interactiveMessageTemplates/getTemplates']);

onMounted(() => {
    store.dispatch('interactiveMessageTemplates/fetchTemplates');
});

watch(() => props.show, (val) => {
    if (val) {
        store.dispatch('interactiveMessageTemplates/fetchTemplates');
    }
});

function loadTemplate(tpl) {
    const p = tpl.payload || {};
    messageType.value = tpl.message_type || 'button';

    headerText.value = p.header?.text || '';
    bodyText.value = p.body?.text || '';
    footerText.value = p.footer?.text || '';

    if (tpl.message_type === 'button') {
        buttons.value = (p.action?.buttons || []).map((b, i) => ({
            id: b.reply?.id || `btn-${i + 1}`,
            title: b.reply?.title || '',
        }));
        if (!buttons.value.length) buttons.value = [{ id: 'btn-1', title: '' }];
    } else {
        listButtonText.value = p.action?.button || 'Ver opções';
        sections.value = (p.action?.sections || []).map((s, si) => ({
            title: s.title || '',
            rows: (s.rows || []).map((r, ri) => ({
                id: r.id || `s${si}-row-${ri + 1}`,
                title: r.title || '',
                description: r.description || '',
            })),
        }));
        if (!sections.value.length) {
            sections.value = [{ title: '', rows: [{ id: 'row-1', title: '', description: '' }] }];
        }
    }

    showTemplateMenu.value = false;
    useAlert('Modelo carregado!');
}

async function saveTemplate() {
    if (!saveName.value.trim()) return;
    isSaving.value = true;
    try {
        const payload = buildPayload();
        await store.dispatch('interactiveMessageTemplates/createTemplate', {
            name: saveName.value.trim(),
            message_type: messageType.value,
            payload,
            scope: saveScope.value,
        });
        useAlert('Modelo salvo com sucesso!');
        showSaveDialog.value = false;
        saveName.value = '';
    } catch (e) {
        useAlert(e?.message || 'Erro ao salvar modelo');
    } finally {
        isSaving.value = false;
    }
}

async function deleteTemplate(id) {
    try {
        await store.dispatch('interactiveMessageTemplates/deleteTemplate', id);
        useAlert('Modelo excluído');
    } catch (e) {
        useAlert('Erro ao excluir modelo');
    }
}

// ===== HELPERS =====
function generateId(prefix, idx) {
    return `${prefix}-${idx + 1}`;
}

// Buttons
function addButton() {
    if (buttons.value.length >= 3) return;
    buttons.value.push({ id: generateId('btn', buttons.value.length), title: '' });
}
function removeButton(idx) {
    if (buttons.value.length <= 1) return;
    buttons.value.splice(idx, 1);
}

// Sections
function addSection() {
    if (sections.value.length >= 10) return;
    sections.value.push({
        title: '',
        rows: [{ id: generateId('row', 0), title: '', description: '' }],
    });
}
function removeSection(sIdx) {
    if (sections.value.length <= 1) return;
    sections.value.splice(sIdx, 1);
}

// Rows
function addRow(sIdx) {
    const totalRows = sections.value.reduce((sum, s) => sum + s.rows.length, 0);
    if (totalRows >= 20) return;
    const section = sections.value[sIdx];
    section.rows.push({
        id: generateId(`s${sIdx}-row`, section.rows.length),
        title: '',
        description: '',
    });
}
function removeRow(sIdx, rIdx) {
    if (sections.value[sIdx].rows.length <= 1) return;
    sections.value[sIdx].rows.splice(rIdx, 1);
}

function moveRowUp(sIdx, rIdx) {
    if (rIdx <= 0) return;
    const rows = sections.value[sIdx].rows;
    [rows[rIdx - 1], rows[rIdx]] = [rows[rIdx], rows[rIdx - 1]];
}

function moveRowDown(sIdx, rIdx) {
    const rows = sections.value[sIdx].rows;
    if (rIdx >= rows.length - 1) return;
    [rows[rIdx], rows[rIdx + 1]] = [rows[rIdx + 1], rows[rIdx]];
}

// ===== VALIDATION =====
const isValid = computed(() => {
    if (!bodyText.value.trim()) return false;
    if (headerText.value.length > 60) return false;
    if (bodyText.value.length > 1024) return false;
    if (footerText.value.length > 60) return false;

    if (messageType.value === 'button') {
        return buttons.value.length >= 1 &&
            buttons.value.every(b => b.title.trim() && b.title.length <= 20);
    }

    // list
    if (!listButtonText.value.trim() || listButtonText.value.length > 20) return false;
    return sections.value.every(s =>
        s.rows.length >= 1 &&
        s.rows.every(r => r.title.trim() && r.title.length <= 24 && r.description.length <= 72)
    );
});

// ===== PAYLOAD =====
function buildPayload() {
    const interactive = {
        type: messageType.value,
        body: { text: bodyText.value.trim() },
    };

    if (headerText.value.trim()) {
        interactive.header = { type: 'text', text: headerText.value.trim() };
    }
    if (footerText.value.trim()) {
        interactive.footer = { text: footerText.value.trim() };
    }

    if (messageType.value === 'button') {
        interactive.action = {
            buttons: buttons.value.map((b, i) => ({
                type: 'reply',
                reply: { id: b.id || generateId('btn', i), title: b.title.trim() },
            })),
        };
    } else {
        interactive.action = {
            button: listButtonText.value.trim(),
            sections: sections.value.map(s => ({
                title: s.title.trim() || undefined,
                rows: s.rows.map((r, ri) => ({
                    id: r.id || generateId('row', ri),
                    title: r.title.trim(),
                    description: r.description.trim() || undefined,
                })),
            })),
        };
    }

    return interactive;
}

function onSend() {
    if (!isValid.value) return;
    emit('send', {
        content: bodyText.value.trim(),
        contentType: 'interactive_message',
        contentAttributes: { interactive: buildPayload() },
    });
    resetForm();
}

function onClose() {
    emit('close');
}

function resetForm() {
    messageType.value = 'button';
    headerText.value = '';
    bodyText.value = '';
    footerText.value = '';
    buttons.value = [{ id: 'btn-1', title: '' }];
    listButtonText.value = 'Ver opções';
    sections.value = [{ title: '', rows: [{ id: 'row-1', title: '', description: '' }] }];
}

// Reset on type change
watch(messageType, () => {
    buttons.value = [{ id: 'btn-1', title: '' }];
    sections.value = [{ title: '', rows: [{ id: 'row-1', title: '', description: '' }] }];
});

// ===== PREVIEW HELPERS =====
const previewButtons = computed(() =>
    buttons.value.filter(b => b.title.trim()).map(b => b.title.trim())
);
const previewSections = computed(() =>
    sections.value.map(s => ({
        title: s.title.trim(),
        rows: s.rows.filter(r => r.title.trim()),
    })).filter(s => s.rows.length > 0)
);
</script>

<template>
    <woot-modal :show="show" @close="onClose" class="interactive-message-modal">
        <div class="flex flex-col h-full max-h-[85vh]">
            <div class="flex items-center px-6 py-4 border-b border-n-weak">
                <div class="flex items-center gap-2">
                    <span class="i-lucide-smartphone size-5 text-green-600" />
                    <h2 class="text-base font-semibold text-n-slate-12">Mensagem Interativa</h2>
                </div>
            </div>

            <div class="flex items-center justify-between px-6 py-2 border-b border-n-weak bg-n-alpha-1">
                <div class="flex items-center gap-2">
                    <div class="relative">
                        <NextButton label="Modelos" slate faded xs icon="i-lucide-folder-open"
                            @click="showTemplateMenu = !showTemplateMenu" />
                        <div v-if="showTemplateMenu"
                            class="absolute left-0 top-full mt-1 w-72 rounded-lg border border-n-weak bg-n-background shadow-lg z-50 overflow-hidden">
                            <div class="px-3 py-2 border-b border-n-weak bg-n-alpha-1">
                                <p class="text-xs font-semibold text-n-slate-12">Modelos Salvos</p>
                            </div>
                            <div class="max-h-64 overflow-y-auto">
                                <div v-if="!templates.length" class="px-3 py-4 text-center text-xs text-n-slate-10">
                                    Nenhum modelo salvo ainda
                                </div>
                                <div v-for="tpl in templates" :key="tpl.id"
                                    class="flex items-center justify-between px-3 py-2 hover:bg-n-alpha-2 transition-colors cursor-pointer group border-b border-n-weak last:border-b-0">
                                    <button class="flex-1 text-left cursor-pointer" @click="loadTemplate(tpl)">
                                        <p class="text-xs font-medium text-n-slate-12 truncate">{{ tpl.name }}</p>
                                        <div class="flex items-center gap-1.5 mt-0.5">
                                            <span class="text-[10px] px-1 py-0.5 rounded bg-n-alpha-2 text-n-slate-10">
                                                {{ tpl.message_type === 'button' ? 'Botões' : 'Lista' }}
                                            </span>
                                            <span class="text-[10px] text-n-slate-9">
                                                {{ tpl.scope === 'personal' ? '🔒 Pessoal' : '🌐 Global' }}
                                            </span>
                                        </div>
                                    </button>
                                    <button
                                        class="opacity-0 group-hover:opacity-100 text-n-slate-10 hover:text-red-500 cursor-pointer p-1 transition-opacity"
                                        @click.stop="deleteTemplate(tpl.id)">
                                        <span class="i-lucide-trash-2 size-3" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <NextButton label="Salvar como Modelo" slate faded xs icon="i-lucide-save"
                        @click="showSaveDialog = !showSaveDialog" />
                </div>
            </div>

            <div v-if="showSaveDialog" class="px-6 py-2.5 border-b border-n-weak bg-n-alpha-1">
                <div class="flex items-center gap-2">
                    <input v-model="saveName"
                        class="flex-1 min-w-0 h-7 px-3 rounded-lg border border-n-weak bg-n-background text-xs text-n-slate-12 outline-none focus:border-green-500"
                        placeholder="Nome do modelo" @keydown.enter="saveTemplate" />
                    <button
                        class="flex-shrink-0 h-7 px-2.5 rounded-lg text-[11px] font-medium cursor-pointer transition-colors"
                        :class="saveScope === 'global' ? 'bg-green-600 text-white' : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'"
                        @click="saveScope = 'global'">
                        🌐 Global
                    </button>
                    <button
                        class="flex-shrink-0 h-7 px-2.5 rounded-lg text-[11px] font-medium cursor-pointer transition-colors"
                        :class="saveScope === 'personal' ? 'bg-green-600 text-white' : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'"
                        @click="saveScope = 'personal'">
                        🔒 Pessoal
                    </button>
                    <NextButton class="flex-shrink-0" label="Salvar" teal xs :disabled="!saveName.trim() || isSaving"
                        :is-loading="isSaving" @click="saveTemplate" />
                    <button
                        class="flex-shrink-0 h-7 w-7 flex items-center justify-center rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 cursor-pointer transition-colors"
                        @click="showSaveDialog = false">
                        <span class="i-lucide-x size-4.5" />
                    </button>
                </div>
            </div>

            <div class="flex flex-1 overflow-hidden">
                <div class="flex-1 overflow-y-auto p-6 space-y-4">
                    <div class="flex gap-2">
                        <button class="flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all cursor-pointer"
                            :class="messageType === 'button' ? 'bg-green-600 text-white' : 'bg-n-background text-n-slate-11 border border-n-weak'"
                            @click="messageType = 'button'">
                            <span class="i-lucide-square size-3.5 inline-block align-middle mr-1" />
                            Botões de Resposta
                        </button>
                        <button class="flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all cursor-pointer"
                            :class="messageType === 'list' ? 'bg-green-600 text-white' : 'bg-n-background text-n-slate-11 border border-n-weak'"
                            @click="messageType = 'list'">
                            <span class="i-lucide-list size-3.5 inline-block align-middle mr-1" />
                            Lista
                        </button>
                    </div>

                    <div>
                        <label class="text-xs font-medium text-n-slate-10 mb-1 block">
                            Cabeçalho <span class="text-n-slate-9">(opcional, max 60)</span>
                        </label>
                        <input v-model="headerText"
                            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 outline-none focus:border-green-500"
                            placeholder="Título da mensagem" maxlength="60" />
                        <span v-if="headerText.length > 50" class="text-[10px] text-amber-600">{{ 60 - headerText.length
                        }} restantes</span>
                    </div>

                    <div>
                        <label class="text-xs font-medium text-n-slate-10 mb-1 block">
                            Corpo da mensagem * <span class="text-n-slate-9">(max 1024)</span>
                        </label>
                        <textarea v-model="bodyText"
                            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 outline-none focus:border-green-500 resize-none"
                            rows="3" placeholder="Escreva sua mensagem aqui..." maxlength="1024" />
                        <span v-if="bodyText.length > 900" class="text-[10px] text-amber-600">{{ 1024 - bodyText.length
                        }} restantes</span>
                    </div>

                    <div>
                        <label class="text-xs font-medium text-n-slate-10 mb-1 block">
                            Rodapé <span class="text-n-slate-9">(opcional, max 60)</span>
                        </label>
                        <input v-model="footerText"
                            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 outline-none focus:border-green-500"
                            placeholder="Texto de rodapé" maxlength="60" />
                    </div>

                    <div v-if="messageType === 'button'" class="border border-n-weak rounded-lg p-4">
                        <div class="flex items-center justify-between mb-3">
                            <p
                                class="text-xs font-bold text-n-slate-12 uppercase tracking-wider flex items-center gap-1.5">
                                <span class="i-lucide-square size-3.5 text-green-600" /> Botões de Resposta
                            </p>
                            <button v-if="buttons.length < 3"
                                class="text-[11px] font-medium cursor-pointer text-green-600 hover:text-green-700"
                                @click="addButton">
                                + Adicionar botão
                            </button>
                        </div>
                        <p class="text-[10px] text-n-slate-10 mb-3">Mínimo 1, máximo 3 botões. Cada título até 20
                            caracteres.</p>
                        <div v-for="(btn, idx) in buttons" :key="idx" class="flex items-center gap-2 mb-2">
                            <input v-model="btn.title"
                                class="flex-1 px-3 py-1.5 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 outline-none focus:border-green-500"
                                :placeholder="`Botão ${idx + 1}`" maxlength="20" />
                            <span class="text-[10px] text-n-slate-9 w-6 text-right">{{ btn.title.length }}/20</span>
                            <button v-if="buttons.length > 1" class="text-n-slate-10 hover:text-red-500 cursor-pointer"
                                @click="removeButton(idx)">
                                <span class="i-lucide-trash-2 size-3.5" />
                            </button>
                        </div>
                    </div>

                    <div v-if="messageType === 'list'" class="border border-n-weak rounded-lg p-4">
                        <div class="flex items-center justify-between mb-3">
                            <p
                                class="text-xs font-bold text-n-slate-12 uppercase tracking-wider flex items-center gap-1.5">
                                <span class="i-lucide-list size-3.5 text-green-600" /> Configuração da Lista
                            </p>
                        </div>

                        <div class="mb-4">
                            <label class="text-xs text-n-slate-10 mb-1 block">Texto do botão * (max 20)</label>
                            <input v-model="listButtonText"
                                class="w-full px-3 py-1.5 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 outline-none focus:border-green-500"
                                placeholder="Ver opções" maxlength="20" />
                        </div>

                        <div v-for="(section, sIdx) in sections" :key="sIdx"
                            class="mb-3 border border-n-weak rounded-lg p-3">
                            <div class="flex items-center justify-between mb-2">
                                <label class="text-xs font-medium text-n-slate-10">Seção {{ sIdx + 1 }}</label>
                                <button v-if="sections.length > 1"
                                    class="text-[10px] text-red-500 hover:text-red-600 cursor-pointer"
                                    @click="removeSection(sIdx)">
                                    Remover seção
                                </button>
                            </div>
                            <input v-model="section.title"
                                class="w-full px-3 py-1.5 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 outline-none focus:border-green-500 mb-2"
                                placeholder="Título da seção (opcional)" maxlength="24" />

                            <div v-for="(row, rIdx) in section.rows" :key="rIdx" class="flex gap-2 mb-2 items-start">
                                <div class="flex-1 space-y-1">
                                    <input v-model="row.title"
                                        class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-12 outline-none focus:border-green-500"
                                        :placeholder="`Título do item ${rIdx + 1} (max 24)`" maxlength="24" />
                                    <input v-model="row.description"
                                        class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-10 outline-none focus:border-green-500"
                                        :placeholder="`Descrição (opcional, max 72)`" maxlength="72" />
                                </div>
                                <div class="flex flex-col items-center gap-0.5 mt-1">
                                    <button v-if="rIdx > 0"
                                        class="text-n-slate-10 hover:text-green-600 cursor-pointer transition-colors"
                                        title="Mover para cima"
                                        @click="moveRowUp(sIdx, rIdx)">
                                        <span class="i-lucide-arrow-up size-3.5" />
                                    </button>
                                    <button v-if="rIdx < section.rows.length - 1"
                                        class="text-n-slate-10 hover:text-green-600 cursor-pointer transition-colors"
                                        title="Mover para baixo"
                                        @click="moveRowDown(sIdx, rIdx)">
                                        <span class="i-lucide-arrow-down size-3.5" />
                                    </button>
                                </div>
                                <button v-if="section.rows.length > 1"
                                    class="text-n-slate-10 hover:text-red-500 cursor-pointer mt-1"
                                    @click="removeRow(sIdx, rIdx)">
                                    <span class="i-lucide-x size-3.5" />
                                </button>
                            </div>
                            <button
                                class="text-[10px] font-medium cursor-pointer text-green-600 hover:text-green-700 flex items-center gap-1"
                                @click="addRow(sIdx)">
                                <span class="i-lucide-plus size-3" /> Adicionar item
                            </button>
                        </div>
                        <button v-if="sections.length < 10"
                            class="text-xs font-medium cursor-pointer text-green-600 hover:text-green-700 flex items-center gap-1"
                            @click="addSection">
                            <span class="i-lucide-plus size-3.5" /> Adicionar seção
                        </button>
                    </div>
                </div>

                <div class="w-80 border-l border-n-weak bg-n-background p-4 overflow-y-auto flex-shrink-0">
                    <p
                        class="text-xs font-bold text-n-slate-10 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <span class="i-lucide-eye size-3.5" /> Preview
                    </p>

                    <div class="rounded-2xl overflow-hidden" style="background: #e5ddd5;">
                        <div class="p-3 min-h-[200px]">
                            <div class="rounded-lg p-3 max-w-[260px] shadow-sm" style="background: #dcf8c6;">
                                <p v-if="headerText.trim()" class="text-xs font-bold mb-1" style="color: #303030;">
                                    {{ headerText }}
                                </p>
                                <p class="text-xs leading-relaxed whitespace-pre-wrap" style="color: #303030;">
                                    {{ bodyText || 'Corpo da mensagem...' }}
                                </p>
                                <p v-if="footerText.trim()" class="text-[10px] mt-2" style="color: #8696a0;">
                                    {{ footerText }}
                                </p>
                            </div>

                            <div v-if="messageType === 'button' && previewButtons.length > 0"
                                class="mt-1 max-w-[260px]">
                                <div v-for="(label, idx) in previewButtons" :key="idx"
                                    class="rounded-lg py-2 px-3 text-center text-xs font-medium mt-1 shadow-sm"
                                    style="background: #fff; color: #00a884;">
                                    {{ label }}
                                </div>
                            </div>

                            <div v-if="messageType === 'list' && listButtonText.trim()" class="mt-1 max-w-[260px]">
                                <div class="rounded-lg py-2 px-3 text-center text-xs font-medium shadow-sm flex items-center justify-center gap-1"
                                    style="background: #fff; color: #00a884;">
                                    <span class="i-lucide-list size-3.5" />
                                    {{ listButtonText }}
                                </div>
                                <div v-if="previewSections.length > 0" class="mt-2 rounded-lg overflow-hidden shadow-sm"
                                    style="background: #fff;">
                                    <div v-for="(sec, sIdx) in previewSections" :key="sIdx">
                                        <div v-if="sec.title" class="px-3 py-1.5 text-[10px] font-bold uppercase"
                                            style="color: #00a884; background: #f0f2f5;">
                                            {{ sec.title }}
                                        </div>
                                        <div v-for="(row, rIdx) in sec.rows" :key="rIdx"
                                            class="px-3 py-2 border-b last:border-b-0" style="border-color: #f0f2f5;">
                                            <p class="text-xs font-medium" style="color: #303030;">{{ row.title }}</p>
                                            <p v-if="row.description" class="text-[10px]" style="color: #8696a0;">{{
                                                row.description }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <p class="text-[10px] text-n-slate-10 mt-2 text-center">
                        Visualização aproximada de como a mensagem aparecerá no WhatsApp
                    </p>
                </div>
            </div>

            <div class="flex items-center justify-end gap-2 px-6 py-4 border-t border-n-weak">
                <NextButton label="Cancelar" slate faded sm @click="onClose" />
                <NextButton label="Enviar Mensagem" teal sm :disabled="!isValid" @click="onSend" />
            </div>
        </div>
    </woot-modal>
</template>

<style scoped>
.interactive-message-modal :deep(.modal-container) {
    max-width: 800px;
    width: 95vw;
}
</style>
